/**
 * Live class scheduling API endpoints (Phase 12)
 */

import { fetchApi } from '../core';
import type {
  CancelClassSessionResponse,
  ClassSessionListResponse,
  ClassSessionQueryParams,
  ClassSessionResponse,
  CreateClassSessionRequest,
  CreatedClassSessionsResponse,
  MarkAttendanceRequest,
  SessionAttendanceResponse,
  UpdateClassSessionRequest,
} from '../types';

function buildQuery(params: ClassSessionQueryParams = {}): string {
  const search = new URLSearchParams();
  if (params.course_id) search.set('course_id', params.course_id);
  if (params.status) search.set('status', params.status);
  if (typeof params.from_ms === 'number') search.set('from_ms', String(params.from_ms));
  if (typeof params.to_ms === 'number') search.set('to_ms', String(params.to_ms));
  if (typeof params.page === 'number') search.set('page', String(params.page));
  if (typeof params.limit === 'number') search.set('limit', String(params.limit));
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export function listClassSessions(
  params: ClassSessionQueryParams = {},
  componentName: string = 'Unknown'
) {
  return fetchApi<ClassSessionListResponse>(
    `/class-sessions${buildQuery(params)}`,
    {},
    componentName
  );
}

/** The caller's own schedule: sessions for courses they're enrolled in. */
export function listMyClassSessions(
  params: ClassSessionQueryParams = {},
  componentName: string = 'Unknown'
) {
  return fetchApi<ClassSessionListResponse>(
    `/class-sessions/mine${buildQuery(params)}`,
    {},
    componentName
  );
}

/** Schedule one session, or — when `recurrence` is set — a series. */
export function createClassSession(
  data: CreateClassSessionRequest,
  componentName: string = 'Unknown'
) {
  return fetchApi<CreatedClassSessionsResponse>(
    '/class-sessions',
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function getClassSession(id: string, componentName: string = 'Unknown') {
  return fetchApi<ClassSessionResponse>(`/class-sessions/${id}`, {}, componentName);
}

export function updateClassSession(
  id: string,
  data: UpdateClassSessionRequest,
  componentName: string = 'Unknown'
) {
  return fetchApi<ClassSessionResponse>(
    `/class-sessions/${id}`,
    { method: 'PUT', body: JSON.stringify(data) },
    componentName
  );
}

/** A session's roster (from the course) joined with recorded attendance. */
export function getSessionAttendance(sessionId: string, componentName: string = 'Unknown') {
  return fetchApi<SessionAttendanceResponse>(
    `/class-sessions/${sessionId}/attendance`,
    {},
    componentName
  );
}

/** Record attendance for some or all of a session's roster. */
export function markSessionAttendance(
  sessionId: string,
  data: MarkAttendanceRequest,
  componentName: string = 'Unknown'
) {
  return fetchApi<SessionAttendanceResponse>(
    `/class-sessions/${sessionId}/attendance`,
    { method: 'PUT', body: JSON.stringify(data) },
    componentName
  );
}

/** Cancel one session, or its whole series. Returns how many were cancelled. */
export function cancelClassSession(
  id: string,
  scope: 'single' | 'series' = 'single',
  reason: string | undefined,
  componentName: string = 'Unknown'
) {
  return fetchApi<CancelClassSessionResponse>(
    `/class-sessions/${id}/cancel`,
    { method: 'POST', body: JSON.stringify({ scope, reason }) },
    componentName
  );
}
